import { useState } from 'react';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import { useTranslation } from '@hooks';
import { PhoneFrame, BrandAvatar } from './styles';
import { hasMediaContent, truncate } from './utils';
import { FbLikeIcon, FbCommentIcon, FbShareIcon } from './PlatformIcons';
import MediaSlide from './MediaSlide';

const FB_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const TRUNCATE_AT = 280;

const Card = styled(PhoneFrame)({ fontFamily: FB_FONT, color: '#1c1e21' });

const HeaderRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 12px 8px',
});

const HeaderTitleColumn = styled('div')({ flex: 1, minWidth: 0 });

const PageName = styled('div')({
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '18px',
    color: '#050505',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Meta = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    lineHeight: '14px',
    color: '#65676b',
    marginTop: 2,
});

const Body = styled('div')({
    padding: '0 12px 10px',
    fontSize: 15,
    lineHeight: '20px',
    color: '#050505',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
});

const SeeMore = styled('button')({
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 15,
    color: '#65676b',
    cursor: 'pointer',
    marginLeft: 4,
});

const ReactionsRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-around',
    borderTop: '1px solid #ced0d4',
    padding: '4px 0',
});

const ReactionButton = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#65676b',
    padding: '6px 8px',
    fontSize: 13,
    fontWeight: 600,
    '& svg': { fontSize: 20 },
});

const PlatformCardFacebook = ({ pageName, locationLogo, message, media, dateLabel }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const { shown, truncated } = expanded ? { shown: message, truncated: false } : truncate(message, TRUNCATE_AT);

    return (
        <Card>
            <HeaderRow>
                <BrandAvatar name={pageName} image={locationLogo} size={40} color="#1877f2" />
                <HeaderTitleColumn>
                    <PageName>{pageName}</PageName>
                    <Meta>
                        {dateLabel && <span>{dateLabel}</span>}
                        {dateLabel && <span>·</span>}
                        <PublicIcon sx={{ fontSize: 12 }} />
                    </Meta>
                </HeaderTitleColumn>
                <MoreHorizIcon sx={{ color: '#65676b' }} />
                <CloseIcon sx={{ color: '#65676b' }} />
            </HeaderRow>

            {message && (
                <Body>
                    {shown}
                    {truncated && (
                        <SeeMore type="button" onClick={() => setExpanded(true)}>
                            {t('SocialPosts.preview.seeMore')}
                        </SeeMore>
                    )}
                </Body>
            )}

            {hasMediaContent(media) && <MediaSlide media={media} aspectRatio="1 / 1" />}

            <ReactionsRow>
                <ReactionButton>
                    <FbLikeIcon />
                    <span>{t('SocialPosts.preview.like')}</span>
                </ReactionButton>
                <ReactionButton>
                    <FbCommentIcon />
                    <span>{t('SocialPosts.preview.comment')}</span>
                </ReactionButton>
                <ReactionButton>
                    <FbShareIcon />
                    <span>{t('SocialPosts.preview.share')}</span>
                </ReactionButton>
            </ReactionsRow>
        </Card>
    );
};

export default PlatformCardFacebook;
